import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { can } from "@/lib/format";
import Layout from "@/components/Layout";
import { Loading } from "@/components/common";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import VehicleDetail from "@/pages/VehicleDetail";
import Agenda from "@/pages/Agenda";
import Receptions from "@/pages/Receptions";
import ReceptionNew from "@/pages/ReceptionNew";
import ReceptionDetail from "@/pages/ReceptionDetail";
import Quotes from "@/pages/Quotes";
import QuoteDetail from "@/pages/QuoteDetail";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Mechanic from "@/pages/Mechanic";
import MechanicOrder from "@/pages/MechanicOrder";
import Catalog from "@/pages/Catalog";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import Audit from "@/pages/Audit";
import QuoteApproval from "@/pages/QuoteApproval";

function Guard({ perm, children }: { perm: string; children: React.ReactElement }) {
  const { user } = useAuth();
  if (!can(user?.role, perm)) return <Navigate to={user?.role === "mecanicien" ? "/atelier" : "/"} replace />;
  return children;
}

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/connexion" replace />;
  return <Layout />;
}

function Home() {
  const { user } = useAuth();
  if (user?.role === "mecanicien") return <Navigate to="/atelier" replace />;
  return <Dashboard />;
}

const routes: [string, string, () => React.ReactElement][] = [
  ["/clients", "clients", Clients],
  ["/clients/:id", "clients", ClientDetail],
  ["/vehicules/:id", "clients", VehicleDetail],
  ["/agenda", "agenda", Agenda],
  ["/receptions", "reception", Receptions],
  ["/receptions/nouvelle", "reception", ReceptionNew],
  ["/receptions/:id", "reception", ReceptionDetail],
  ["/devis", "quotes", Quotes],
  ["/devis/:id", "quotes", QuoteDetail],
  ["/or", "orders", Orders],
  ["/or/:id", "orders", OrderDetail],
  ["/atelier", "mechanic", Mechanic],
  ["/atelier/:id", "mechanic", MechanicOrder],
  ["/catalogue", "catalog", Catalog],
  ["/factures", "billing", Invoices],
  ["/factures/:id", "billing", InvoiceDetail],
  ["/parametres", "admin", Settings],
  ["/utilisateurs", "admin", Users],
  ["/journal", "admin", Audit],
];

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/connexion" element={<Login />} />
          <Route path="/accord/:token" element={<QuoteApproval />} />
          <Route element={<Protected />}>
            <Route path="/" element={<Home />} />
            {routes.map(([path, perm, C]) => <Route key={path} path={path} element={<Guard perm={perm}><C /></Guard>} />)}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        <Toaster position="top-right" richColors theme="dark" />
      </AuthProvider>
    </BrowserRouter>
  );
}
